import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Modal } from '../../components/Modal';
import Button from '../../components/ui/Button';
import { ErrorState } from '../../components/ui/States';
import {
  RaiseSupplierReturnsBody,
  RaiseSupplierReturnsResult,
  SupplierReturnOffer,
  SupplierReturnOption,
  SupplierReturnRaisedRef,
  SupplierReturnVendorInvoice,
  isLeftOutLine,
  raiseSupplierReturns,
} from '../../lib/creditNoteApi';
import { DEBIT_NOTE_STATUS_LABELS, DebitNoteStatus } from '../../lib/debitNoteApi';
import { errorMessageOf } from '../debitNotes/debitNoteDisplay';

/**
 * "Return to supplier?" (D44 #9, #40-#44, VR-4): after an Item Return is issued,
 * raise DRAFT vendor returns for the goods.
 *
 *   Goods back into United stock: one goods return per vendor invoice the goods
 *   came in on, its lines as the server worked them out (whole billed units, #43).
 *   The person only chooses which invoices.
 *   Straight to the vendor / not returned: money only. For each credited line the
 *   person says how much to claim from each vendor invoice line it shipped from (#44).
 *
 * Nothing is priced here: the server prices each draft. Rendered only while open.
 */

function openOption(o: { raised: SupplierReturnRaisedRef | null; unavailable: string | null }): boolean {
  return !o.raised && !o.unavailable;
}

function raisableInvoice(vi: SupplierReturnVendorInvoice): boolean {
  return openOption(vi) && vi.lines.some((l) => !isLeftOutLine(l));
}

function optionKey(lineIndex: number, o: SupplierReturnOption): string {
  return `${lineIndex}:${o.vendorInvoiceId}:${o.vendorLineNumber}`;
}

const RaisedLink: React.FC<{ raised: SupplierReturnRaisedRef }> = ({ raised }) => (
  <span>
    Already raised:{' '}
    <Link to={`/credit-notes/vendor/${raised.debitNoteId}`} className="font-medium text-primary-600 hover:underline">
      {raised.debitNoteNumber}
    </Link>
    {raised.status ? ` (${DEBIT_NOTE_STATUS_LABELS[raised.status as DebitNoteStatus] || raised.status})` : ''}
  </span>
);

export const SupplierReturnDialog: React.FC<{
  creditNoteId: string;
  creditNoteNumber: string;
  offer: SupplierReturnOffer;
  onClose: () => void;
  /** Something was raised: the page reads the offer again. */
  onRaised: () => void;
}> = ({ creditNoteId, creditNoteNumber, offer, onClose, onRaised }) => {
  const [ticked, setTicked] = useState<string[]>(() =>
    offer.kind === 'goods' ? offer.vendorInvoices.filter(raisableInvoice).map((vi) => vi.vendorInvoiceId) : []
  );
  const [quantities, setQuantities] = useState<Record<string, string>>(() => {
    const out: Record<string, string> = {};
    if (offer.kind === 'money_only') {
      offer.creditLines.forEach((line, lineIndex) =>
        line.options.forEach((o) => {
          if (openOption(o) && o.suggestedQuantity !== null) out[optionKey(lineIndex, o)] = String(o.suggestedQuantity);
        })
      );
    }
    return out;
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<RaiseSupplierReturnsResult | null>(null);

  const number = creditNoteNumber || 'this credit note';

  const body: RaiseSupplierReturnsBody =
    offer.kind === 'goods'
      ? {
          vendorInvoiceIds: offer.vendorInvoices
            .filter((vi) => raisableInvoice(vi) && ticked.includes(vi.vendorInvoiceId))
            .map((vi) => vi.vendorInvoiceId),
        }
      : {
          splits: offer.creditLines.flatMap((line, lineIndex) =>
            line.options.flatMap((o) => {
              if (!openOption(o)) return [];
              const text = (quantities[optionKey(lineIndex, o)] ?? '').trim();
              // Blank and zero are left out; anything else goes as typed and the server names what is wrong.
              if (text === '' || Number(text) === 0) return [];
              return [
                {
                  creditLineNumber: line.lineNumber,
                  vendorInvoiceId: o.vendorInvoiceId,
                  vendorLineNumber: o.vendorLineNumber,
                  quantity: Number(text),
                },
              ];
            })
          ),
        };
  const nothingChosen = 'vendorInvoiceIds' in body ? body.vendorInvoiceIds.length === 0 : body.splits.length === 0;

  const raise = async () => {
    setBusy(true);
    setError(null);
    try {
      const done = await raiseSupplierReturns(creditNoteId, body);
      setResult(done);
      if (done.raised.length) onRaised();
    } catch (e) {
      setError(errorMessageOf(e, 'The vendor returns could not be raised.'));
    } finally {
      setBusy(false);
    }
  };

  if (result) {
    return (
      <Modal isOpen title="Vendor returns" onClose={onClose} maxWidthClass="max-w-2xl">
        <div className="space-y-3 text-xs text-slate-700" data-testid="supplier-return-result">
          {result.raised.length > 0 ? (
            <>
              <p>
                Raised as {result.raised.length === 1 ? 'a draft' : 'drafts'}. A draft vendor return can be edited before
                it is sent to the vendor.
              </p>
              <ul className="space-y-2">
                {result.raised.map((r) => (
                  <li key={r.debitNoteId} className="rounded-md border border-slate-200 px-3 py-2">
                    <Link
                      to={`/credit-notes/vendor/${r.debitNoteId}`}
                      className="font-medium text-primary-600 hover:underline"
                    >
                      {r.debitNoteNumber}
                    </Link>{' '}
                    on invoice {r.invoiceNumber || '--'}
                    {r.leftOut.map((l, i) => (
                      <div key={`${l.lineNumber}-${i}`} className="mt-0.5 text-amber-700">
                        {l.message}
                      </div>
                    ))}
                  </li>
                ))}
              </ul>
            </>
          ) : (
            <p>No vendor return was raised.</p>
          )}
          {result.skipped.length > 0 && (
            <ul className="space-y-2">
              {result.skipped.map((s) => (
                <li key={s.vendorInvoiceId} className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-amber-800">
                  Invoice {s.invoiceNumber || '--'}: {s.message}
                  {(s.leftOut || []).map((l, i) => (
                    <div key={`${l.lineNumber}-${i}`} className="mt-0.5">
                      {l.message}
                    </div>
                  ))}
                </li>
              ))}
            </ul>
          )}
          <div className="flex justify-end pt-1">
            <Button variant="primary" onClick={onClose}>
              Done
            </Button>
          </div>
        </div>
      </Modal>
    );
  }

  return (
    <Modal isOpen title="Return to supplier?" onClose={onClose} maxWidthClass="max-w-3xl">
      <div className="space-y-3 text-xs text-slate-700">
        {offer.kind === 'goods' ? (
          <p>
            The goods on {number} went back into United stock. A vendor return can be raised for each vendor invoice
            they came in on.
          </p>
        ) : (
          <p>
            The goods on {number} {offer.goodsDestination === 'vendor' ? 'went straight to the vendor' : 'were not returned'},
            so each vendor return claims money only. Say how many of each credited line to claim from the vendor invoice
            lines it was shipped from.
          </p>
        )}
        <p className="text-slate-500">
          Each is raised as a draft vendor return, which can be edited before it is sent to the vendor.
        </p>

        {offer.excluded.length > 0 && (
          <ul className="space-y-1 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-amber-800">
            {offer.excluded.map((x, i) => (
              <li key={`${x.lineNumber}-${i}`}>{x.message}</li>
            ))}
          </ul>
        )}

        {offer.kind === 'goods' &&
          (offer.vendorInvoices.length === 0 ? (
            <p className="text-slate-500">There is no vendor invoice to raise a return on.</p>
          ) : (
            <ul className="space-y-2">
              {offer.vendorInvoices.map((vi) => {
                const tickable = raisableInvoice(vi);
                return (
                  <li key={vi.vendorInvoiceId} className="rounded-md border border-slate-200 px-3 py-2">
                    <label className="flex items-center gap-2">
                      {tickable && (
                        <input
                          type="checkbox"
                          aria-label={`Raise a vendor return on ${vi.invoiceNumber}`}
                          className="h-4 w-4"
                          checked={ticked.includes(vi.vendorInvoiceId)}
                          onChange={(e) =>
                            setTicked((prev) =>
                              e.target.checked
                                ? [...prev.filter((x) => x !== vi.vendorInvoiceId), vi.vendorInvoiceId]
                                : prev.filter((x) => x !== vi.vendorInvoiceId)
                            )
                          }
                        />
                      )}
                      <span>
                        <span className="font-semibold text-slate-800">{vi.invoiceNumber || '--'}</span>
                        {vi.vendorName ? ` · ${vi.vendorName}` : ''}
                        {vi.currency ? ` · ${vi.currency}` : ''}
                      </span>
                    </label>
                    {vi.raised && (
                      <div className="mt-1">
                        <RaisedLink raised={vi.raised} />
                      </div>
                    )}
                    {vi.unavailable && <div className="mt-1 text-amber-700">{vi.unavailable}</div>}
                    {!vi.raised && !vi.unavailable && !tickable && (
                      <div className="mt-1 text-slate-500">Nothing on this invoice can go on a vendor return.</div>
                    )}
                    {vi.lines.length > 0 && (
                      <ul className="mt-1 space-y-0.5 pl-6">
                        {vi.lines.map((l) =>
                          isLeftOutLine(l) ? (
                            <li key={l.lineNumber} className="text-amber-700">
                              {l.leftOut}
                            </li>
                          ) : (
                            <li key={l.lineNumber}>
                              Line {l.lineNumber}
                              {l.description ? `: ${l.description}` : ''} ·{' '}
                              <span className="tabular-nums">
                                {l.caseSeqs.length
                                  ? `${l.caseSeqs.length === 1 ? 'Box' : 'Boxes'} ${l.caseSeqs.join(', ')}`
                                  : `${l.quantity ?? '--'} ${l.unit || ''}`.trim()}
                              </span>
                              {l.stays && <span className="text-slate-500"> ({l.stays})</span>}
                            </li>
                          )
                        )}
                      </ul>
                    )}
                  </li>
                );
              })}
            </ul>
          ))}

        {offer.kind === 'money_only' &&
          (offer.creditLines.length === 0 ? (
            <p className="text-slate-500">No credited line shipped from a vendor invoice line.</p>
          ) : (
            <div className="space-y-3">
              {offer.creditLines.map((line, lineIndex) => (
                <div key={`${line.lineNumber}-${lineIndex}`} className="rounded-md border border-slate-200 px-3 py-2">
                  <div className="mb-1 font-semibold text-slate-800">
                    Line {line.lineNumber}
                    {line.itemName ? `: ${line.itemName}` : ''} · {`${line.quantity} ${line.unit || ''}`.trim()} credited
                  </div>
                  <div className="overflow-x-auto">
                    <table className="data-table data-table-dense">
                      <thead>
                        <tr>
                          <th>Vendor invoice</th>
                          <th>Vendor invoice line</th>
                          <th className="w-56">Quantity to claim</th>
                        </tr>
                      </thead>
                      <tbody>
                        {line.options.map((o) => {
                          const key = optionKey(lineIndex, o);
                          const text = quantities[key] ?? '';
                          const over =
                            o.maxQuantity !== null && text.trim() !== '' && Number(text) > o.maxQuantity;
                          return (
                            <tr key={key}>
                              <td className="align-top">
                                <div className="font-medium text-slate-800">{o.invoiceNumber || '--'}</div>
                                <div className="text-slate-500">
                                  {o.vendorName}
                                  {o.currency ? ` · ${o.currency}` : ''}
                                </div>
                              </td>
                              <td className="align-top">
                                Line {o.vendorLineNumber}
                                {o.description ? `: ${o.description}` : ''}
                              </td>
                              <td className="align-top">
                                {o.raised ? (
                                  <RaisedLink raised={o.raised} />
                                ) : o.unavailable ? (
                                  <span className="text-amber-700">{o.unavailable}</span>
                                ) : (
                                  <>
                                    <div className="flex items-center gap-1">
                                      <input
                                        type="number"
                                        min="0"
                                        step="any"
                                        max={o.maxQuantity ?? undefined}
                                        aria-label={`Quantity to claim on ${o.invoiceNumber} line ${o.vendorLineNumber} for line ${line.lineNumber}`}
                                        aria-invalid={over || undefined}
                                        className={`w-full h-8 rounded-md border px-2 text-xs text-right ${
                                          over ? 'border-red-300' : 'border-slate-200'
                                        }`}
                                        value={text}
                                        placeholder={o.maxQuantity !== null ? `max ${o.maxQuantity}` : ''}
                                        onChange={(e) => setQuantities((prev) => ({ ...prev, [key]: e.target.value }))}
                                      />
                                      <span className="text-slate-500">{o.unit}</span>
                                    </div>
                                    {o.maxQuantity !== null && (
                                      <div className={`mt-0.5 ${over ? 'text-red-700' : 'text-slate-400'}`}>
                                        max {`${o.maxQuantity} ${o.unit || ''}`.trim()} went to the vessel for this line
                                      </div>
                                    )}
                                    {o.creditedInBilledUnits !== null && (
                                      <div className="mt-0.5 text-slate-500">
                                        The credit is {`${o.creditedInBilledUnits} ${o.unit || ''}`.trim()} on this invoice
                                      </div>
                                    )}
                                  </>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}
            </div>
          ))}

        {error && <ErrorState message={error} />}

        <div className="flex justify-end gap-2 pt-1">
          <Button onClick={onClose} disabled={busy}>
            Not now
          </Button>
          <Button variant="primary" onClick={raise} loading={busy} disabled={nothingChosen}>
            Raise vendor returns
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default SupplierReturnDialog;
